/**
 * TelehealthVideoRoomWrapper Component
 *
 * Wrapper that fetches session/encounter data and renders TelehealthVideoRoom
 * Used in tab-based navigation where only sessionId is available
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import TelehealthVideoRoom from './TelehealthVideoRoom';
import { telehealthSessionAPI, telehealthEncounterAPI } from '@/services/api/telehealth';
import { EncounterDocumentation, SessionOutcome } from './types';

interface TelehealthVideoRoomWrapperProps {
  sessionId: number;
  onEndCall: () => void;
  onBackToDashboard: () => void;
}

export default function TelehealthVideoRoomWrapper({
  sessionId,
  onEndCall,
  onBackToDashboard,
}: TelehealthVideoRoomWrapperProps) {
  const qc = useQueryClient();

  // Fetch session details
  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useQuery({
    queryKey: ['telehealth-session', sessionId],
    queryFn: () => telehealthSessionAPI.getSession(sessionId),
    refetchInterval: 10000, // Poll for updates
  });

  // Fetch encounter if session has one
  const {
    data: encounter,
    isLoading: encounterLoading,
  } = useQuery({
    queryKey: ['telehealth-encounter', session?.encounter_id],
    queryFn: () => telehealthEncounterAPI.getEncounter(session!.encounter_id!),
    enabled: !!session?.encounter_id,
  });

  // Update encounter mutation
  const updateEncounterMutation = useMutation({
    mutationFn: (updates: Partial<EncounterDocumentation>) =>
      telehealthEncounterAPI.updateEncounter(encounter!.id, { documentation: updates }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['telehealth-encounter', session?.encounter_id] });
    },
  });

  const handleEndCall = (outcome: SessionOutcome, notes?: string) => {
    // Navigate back to dashboard after session ends
    onEndCall();
  };

  const handleDocumentationUpdate = (updates: Partial<EncounterDocumentation>) => {
    if (encounter) {
      updateEncounterMutation.mutate(updates);
    }
  };

  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-white">Loading session...</p>
        </div>
      </div>
    );
  }

  if (sessionError || !session) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Session Not Found</h2>
          <p className="text-gray-400 mb-6">
            The telehealth session could not be loaded. It may have been cancelled or already completed.
          </p>
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Note: a 'scheduled'/'waiting' session is NOT dead-ended here. Entering the
  // video room starts the call — TelehealthVideoRoom.initializeVideo creates the
  // Twilio room, which the backend advances to 'in_progress' (see create_video_room),
  // then fetches a token and connects. Blocking here forced a separate waiting-room
  // + /start step (with a coordinator-match gate) that broke one-click join.

  // If session is completed/cancelled, show message
  if (['completed', 'no_show', 'cancelled', 'technical_failure'].includes(session.status)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Session Ended</h2>
          <p className="text-gray-400 mb-6">
            This telehealth session has already ended with status: {session.status.replace('_', ' ')}
          </p>
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Create a placeholder encounter if none exists
  const encounterData = encounter || {
    id: 0,
    cbo_id: session.cbo_id,
    telehealth_session_id: session.id,
    member_id: session.member_id,
    documenting_user_id: session.care_coordinator_id,
    encounter_date: new Date().toISOString().split('T')[0],
    encounter_type: 'telehealth_visit' as const,
    place_of_service_code: '02',
    sdoh_domains_addressed: [],
    referrals_generated: 0,
    verbal_consent_obtained: false,
    status: 'draft' as const,
    drafted_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  return (
    <TelehealthVideoRoom
      session={session}
      encounter={encounterData}
      onEndCall={handleEndCall}
      onDocumentationUpdate={handleDocumentationUpdate}
    />
  );
}
